import React, {useEffect, useMemo, useState} from 'react';
import {useCredential, useList} from 'components/hooks';
import {useTranslation} from "react-i18next";
import { useStyle } from './styles';
import {useForm, FormProvider} from "react-hook-form";
import {Backdrop, Button, CircularProgress, Grid, Paper, Typography, Divider, Skeleton} from "@mui/material";
import {ROUTES} from "../../../consts";
import {Input} from "../../../components/layout";
import {credentialPolicies} from "../../../helpers";
import {FormChipList, HeadlinePage} from "../../../components/common";
import { isVisible } from '@testing-library/user-event/dist/utils';
import { IconButton, InputAdornment } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

export const CredentialFormDumb = ({  defaultValues, handleOnSubmit, isEdit, canEdit=false}) => {
  const [showPassword, setShowPassword] = useState(false);
  const { isLoaded } = useCredential();
  const [t] = useTranslation();
  const classes = useStyle();
  const { getContentList, content } = useList('companies');
  const [companyOptions, setCompanyOptions] = useState([]);
  const [typeFields, setTypeFields] = useState(defaultValues.type);

  const typeOptions = [
    {value: 1, label: 'credentialList.components.types.network'},
    {value: 2, label: 'credentialList.components.types.machine'},
    {value: 3, label: 'credentialList.components.types.token'},
    {value: 4, label: 'credentialList.components.types.pem'}
  ];

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const methods = useForm({
    defaultValues: useMemo(() => {
      return defaultValues;
    }, [defaultValues])
  });

  const { reset, control, handleSubmit, setValue, getValues } = methods;
  useEffect(() => {
    getContentList({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (content) {
      const companies = content.map(c => ({value: c.company_id, label: c.name}));
      setCompanyOptions(companies);
      setValue('company', companies && companies.length>0 ? companies[0].value:null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  useEffect(() => {
    if (isEdit) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  const fields = [
    { policy: credentialPolicies.name },
    { policy: credentialPolicies.description },
  ];

  const fieldTags = [
    { ...credentialPolicies.tags, defaultValue: getValues('tags') }
  ]

  const showTypeField = (permitsTypesIds) => {
    return permitsTypesIds.includes(getValues('type'));
  }

  const selectFields = [
    { policy: credentialPolicies.user, permitsTypesIds: [1,2,3,4]},
    { policy: credentialPolicies.password, permitsTypesIds: [1,2,4], type: 'pass'}, 
    { policy: credentialPolicies.enabled, permitsTypesIds: [1], type: 'pass'},
    { policy: credentialPolicies.privateKey, permitsTypesIds: [2]},
    { policy: credentialPolicies.token, permitsTypesIds: [3],  type: 'pass'},
    { policy: credentialPolicies.sudo, permitsTypesIds: [2], type: 'pass'},
  ]


  const handleOnChangeTag = (name) => {
    return (value) => setValue(name, value);
  };

  const handleOnChangeType = (event) => {
    if (event.target.value) {
      setTypeFields(event.target.value);
    }
    setValue(credentialPolicies.user.name, '');
    setValue(credentialPolicies.password.name, '');
    setValue(credentialPolicies.enabled.name, '');
    setValue(credentialPolicies.privateKey.name, '');
    setValue(credentialPolicies.token.name, '');
    setValue(credentialPolicies.sudo.name, '');
  }

  const prepareData = (data) => {
    handleOnSubmit({...defaultValues, ...data})
  }

  const TypeFieldComponent = ({typeField}) => {
    const fieldsToShow =  selectFields.filter(field => field.permitsTypesIds.some((value) => value === typeField));

    return (
      <>
        { fieldsToShow.map((field) => (
          <React.Fragment key={field.policy.id}>
            <Grid item xs={12} sm={6} md={4} lg={4} key={field.id}>
              <Input
                {...field.policy}
                control={control}
                options={field.options}
                sx={{ width: '100%' }}
                InputProps={ field?.type !== "pass" ? {} : {
                  endAdornment: <InputAdornment position="end">
                    <IconButton
                     aria-label="toggle password visibility"
                     onClick={() => setShowPassword(!showPassword)}
                     onMouseDown={handleMouseDownPassword}
                     edge="end"
                    >
                     {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                 </InputAdornment>
               }}
              />
            </Grid>
          </React.Fragment>
        ))}
      </>
    )
  }


  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  return (
   <>
     <Backdrop
       sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
       open={isLoaded.post || isLoaded.put}
     >
       <CircularProgress color="primary" />
     </Backdrop>

     <HeadlinePage
       title={isEdit ? 'credentialEdit.headline' : 'credentialCreate.headline'}
       goBack={ROUTES.CREDENTIAL_LIST}
     />

     <FormProvider { ...methods }>
       <Paper className={classes.paperStyle}>
         <form onSubmit={handleSubmit(prepareData)} onKeyDown={handleKeyDown}>
           <Typography variant="h6" sx={{ padding: '8px 0px' }}>
             {t('credentialList.form.title')}
           </Typography>

           <Grid container spacing={2}>
             {fields.map((field) => (
               <Grid item xs={12} sm={12} md={6} lg={4} key={field.policy.id}>
                 <Input
                   {...field.policy}
                   control={control}
                   options={field.options}
                   sx={{ width: '100%' }}
                 />
               </Grid>
             ))}

             <Grid item xs={12} sm={12} md={6} lg={4} key={credentialPolicies.company.id}>
               <Input
                 { ...credentialPolicies.company }
                 control={ control }
                 options={ companyOptions }
                 sx={{ width: '100%' }}
               />
             </Grid>

             { fieldTags.map((chipField, index) => (
               <Grid item xs={12} sm={12} md={6} lg={4} key={index}>
                 <FormChipList
                   {...chipField}
                   control={control}
                   onChange={handleOnChangeTag(chipField.name)}
                 />
               </Grid>
             ))}
           </Grid>

           <Grid container spacing={2} sx={{alignItems: 'flex-end', padding: '8px 0px'}}>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Divider/>
              <Typography variant="h6" sx={{ padding: '8px 0px' }}>
                {t('credentialList.form.subtitle')}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{alignItems: 'flex-start'}}>
            <Grid item xs={6} sm={6} md={6} lg={6} >
              { typeOptions.length > 0 ?
                (
                  <>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <Input
                        { ...credentialPolicies.type }
                        control={ control }
                        options={ typeOptions }
                        sx={{ width: '100%' }}
                        onChange = {handleOnChangeType}
                      />
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <Skeleton
                      sx={{ marginBottom: 1 }}
                      variant="text"
                      animation="wave"
                      width="60%"
                    />
                    <Skeleton height="40px" variant="rounded" animation="wave" />
                  </Grid>
                )
              }
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 4 }}>
            { typeFields &&
              <TypeFieldComponent typeField={typeFields}/>
            }
          </Grid>
          {canEdit?
          <Grid container sx={{ mt: 4 }}>
            <Grid item md={2} lg={3} />
            <Grid item xs={12} sm={12} md={8} lg={6}>
              <Button variant="contained" sx={{ width: '100%' }} type='submit'>
                { isEdit
                  ? t('credentialEdit.components.submit')
                  : t('credentialCreate.components.submit')}
              </Button>
            </Grid>
            <Grid item md={2} lg={3} />
          </Grid>:null}

         </form>
       </Paper>
     </FormProvider>
   </>
  )
}
